import { useState } from 'react'
import { clientEvalFormula } from './useRecipeState'

const TOKEN_TYPES = [
  { key: 'input', label: 'Input Key', className: 'tok-input' },
  { key: 'number', label: 'Number', className: 'tok-num' },
  { key: 'op', label: 'Operator', className: 'tok-op' },
  { key: 'fn', label: 'Function', className: 'tok-fn' }
]

const OPS = ['+', '-', '*', '/']
const FNS = ['ceil', 'floor', 'max', 'min']

const FormulaBuilder = ({ formula, onChange, inputSlots, slotValues }) => {
  const [showAdd, setShowAdd] = useState(false)
  const [addType, setAddType] = useState(null)
  const [numVal, setNumVal] = useState('')

  // Parse formula string into display tokens
  const tokens = parseFormulaTokens(formula || '')

  const evalResult = clientEvalFormula(formula || '', slotValues || {})

  const appendToFormula = (text) => {
    const current = (formula || '').trim()
    const newFormula = current ? `${current} ${text}` : text
    onChange(newFormula)
    setShowAdd(false)
    setAddType(null)
    setNumVal('')
  }

  return (
    <div>
      {/* Token display */}
      <div className="formula-row">
        {tokens.length === 0 && (
          <span style={{ color: 'var(--ink-4)', fontSize: 12, fontStyle: 'italic' }}>Empty formula</span>
        )}
        {tokens.map((tok, i) => (
          <span key={i} className={`tok ${tok.className}`}>{tok.display}</span>
        ))}
      </div>

      {/* Raw formula input */}
      <input
        className="bare mono"
        value={formula || ''}
        onChange={e => onChange(e.target.value)}
        placeholder="e.g. lateral_lf * 4 + ceil(area_sf / 100)"
        style={{ marginTop: 8, fontSize: 12 }}
      />

      {/* Add token controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        {!showAdd ? (
          <button
            className="btn"
            style={{ fontSize: 11, padding: '4px 10px' }}
            onClick={() => setShowAdd(true)}
          >+ token</button>
        ) : (
          <>
            {!addType && TOKEN_TYPES.map(tt => (
              <button
                key={tt.key}
                className="btn"
                style={{ fontSize: 10, padding: '3px 8px' }}
                onClick={() => setAddType(tt.key)}
              >{tt.label}</button>
            ))}

            {addType === 'input' && (inputSlots || []).map(s => (
              <button
                key={s.key}
                className={`tok tok-input`}
                style={{ cursor: 'pointer', border: 'none' }}
                onClick={() => appendToFormula(s.key)}
              >{s.key}</button>
            ))}

            {addType === 'op' && OPS.map(op => (
              <button
                key={op}
                className="tok tok-op"
                style={{ cursor: 'pointer', border: 'none' }}
                onClick={() => appendToFormula(op)}
              >{op}</button>
            ))}

            {addType === 'fn' && FNS.map(fn => (
              <button
                key={fn}
                className="tok tok-fn"
                style={{ cursor: 'pointer', border: 'none' }}
                onClick={() => appendToFormula(`${fn}(`)}
              >{fn}()</button>
            ))}

            {addType === 'number' && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input
                  type="number"
                  className="bare mono"
                  value={numVal}
                  onChange={e => setNumVal(e.target.value)}
                  placeholder="0"
                  style={{ width: 80 }}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && numVal) appendToFormula(numVal) }}
                />
                <button className="btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => { if (numVal) appendToFormula(numVal) }}>Add</button>
              </div>
            )}

            <button
              className="btn"
              style={{ fontSize: 10, padding: '3px 8px' }}
              onClick={() => { setShowAdd(false); setAddType(null); setNumVal('') }}
            >Cancel</button>
          </>
        )}

        {/* Live evaluation */}
        {formula && (
          <span style={{
            marginLeft: 'auto',
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            color: 'var(--primary-2)',
            background: 'var(--primary-soft)',
            padding: '3px 10px',
            borderRadius: 4
          }}>
            = {evalResult.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  )
}

function parseFormulaTokens(str) {
  if (!str) return []
  const tokens = []
  let i = 0
  while (i < str.length) {
    const ch = str[i]
    if (/\s/.test(ch)) { i++; continue }
    if ('+-*/'.includes(ch)) {
      tokens.push({ display: ch, className: 'tok-op' })
      i++; continue
    }
    if (ch === '(' || ch === ')') {
      tokens.push({ display: ch, className: 'tok-paren' })
      i++; continue
    }
    if (/[0-9.]/.test(ch)) {
      let n = ''
      while (i < str.length && /[0-9.]/.test(str[i])) n += str[i++]
      tokens.push({ display: n, className: 'tok-num' })
      continue
    }
    if (/[a-zA-Z_]/.test(ch)) {
      let nm = ''
      while (i < str.length && /[a-zA-Z0-9_]/.test(str[i])) nm += str[i++]
      const lower = nm.toLowerCase()
      if (['ceil', 'floor', 'max', 'min'].includes(lower)) {
        tokens.push({ display: nm, className: 'tok-fn' })
      } else {
        tokens.push({ display: nm, className: 'tok-input' })
      }
      continue
    }
    // comma
    if (ch === ',') {
      tokens.push({ display: ',', className: 'tok-op' })
      i++; continue
    }
    i++
  }
  return tokens
}

export default FormulaBuilder
